import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { IDatabaseItemModalConfig, IModalConfig } from './types';

import { useModal } from '@/hooks';
import { DatabaseItemDeleteModal, DatabaseItemModal } from '@/components/common/resources/modals';

interface IReturn {
  handleCloseModal: () => void;
  handleOpenModal: ({ data, modalType }: IDatabaseItemModalConfig) => void;
}

const useDatabaseItemModal = (): IReturn => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const getModalSettings = useCallback<(config: IDatabaseItemModalConfig) => IModalConfig>(
    config => {
      switch (config.modalType) {
        case 'delete':
          return {
            size: 'm',
            title: t(`resources.modals.title.databaseItem.delete`),
            content: <DatabaseItemDeleteModal modalData={config.data} handleClose={closeModal} />,
          };
        case 'edit':
        case 'new':
          return {
            size: 'l',
            title: t(`resources.modals.title.databaseItem.${config.modalType}`),
            content: <DatabaseItemModal modalConfig={config} handleClose={closeModal} />,
          };
        default:
          throw new Error('Undefined modal type.');
      }
    },
    [closeModal, t],
  );

  const openFolderModal = useCallback<IReturn['handleOpenModal']>(
    config => {
      const modalSettings = getModalSettings(config);
      openModal(modalSettings);
    },
    [getModalSettings, openModal],
  );

  return {
    handleCloseModal: closeModal,
    handleOpenModal: openFolderModal,
  };
};

export { useDatabaseItemModal };
